import { Immutable } from "immer";
import React from "react";

type IProps = Immutable<{
    /** Bare-markup mode for the ``?storybook=1`` fragment pipeline: the
     * fragment writer extracts the body innerHTML and strips scripts, so the
     * body must contain ONLY the block markup — no wrappers, no body
     * attributes, no site furniture. */
    storybook: boolean;
    htmlLang: string;
    bodyId: string;
    legacyJsUrl: string;
    /** Value for the <html> data-design-version attribute, gating
     * version-scoped CSS ([data-design-version] selectors). Omit on sites
     * without a design-version toggle. */
    designVersion?: string;
}> & {
    /** Site-specific pixel-bearing head links (fonts, per-APP_SLUG skins). */
    extraHead?: React.ReactNode;
    /** Site-specific body-top furniture (e.g. sealy's inlined svg symbol
     * sheet, which block markup references via <use>). */
    bodyStart?: React.ReactNode;
    children?: React.ReactNode;
    /** Site-specific invisible body-end furniture (keep-alive / financing
     * modals) mirroring what the site's production PageSkeleton mounts. */
    bodyEnd?: React.ReactNode;
};

/**
 * Full-document shell for the IS_UNIT_TEST-only styleguide screenshot
 * fixtures, rendered natively through reactivated (no Django template shell).
 *
 * The reactivated renderer injects the bundle CSS (React hoists it into
 * <head>) plus the bootstrap scripts and __PRELOADED_PROPS__, so — like the
 * production PageSkeletons — this component must not emit any of those
 * itself. Only the site-variable chrome (lang, body id, skins/fonts,
 * furniture) is supplied by the per-site template wrapper.
 */
export const StyleguideDocument = (props: IProps): React.JSX.Element => {
    if (props.storybook) {
        return (
            <html lang={props.htmlLang}>
                <head>
                    <meta charSet="utf-8" />
                    <title>Styleguide</title>
                </head>
                <body>{props.children}</body>
            </html>
        );
    }
    return (
        <html
            className="no-js"
            lang={props.htmlLang}
            data-design-version={props.designVersion}
        >
            <head>
                <meta charSet="utf-8" />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <title>Styleguide</title>
                {props.extraHead}
            </head>
            <body id={props.bodyId} className="default">
                {props.bodyStart}
                {props.children}
                {props.bodyEnd}
                {/* async (not positional defer): React 19 hoists async script
                    resources, so hydration never pairs this vnode against a
                    foreign body-end sibling (PageSkeleton precedent). */}
                <script async={true} src={props.legacyJsUrl} />
            </body>
        </html>
    );
};
